import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { colors } from '../../../themes/typography';
import { responsiveSpacing } from '../../../themes/responsive';
import { experimentColors } from '../../../themes/experimentColors';

const ExperimentCardSkeleton = () => (
  <Box sx={{
    background: `linear-gradient(135deg, ${colors.background.surface} 0%, ${colors.background.subtle} 100%)`,
    border: `2px solid ${colors.border.light}`,
    borderRadius: '12px',
    p: responsiveSpacing.md,
    position: 'relative'
  }}>
    {/* Product Header Skeleton */}
    <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
    
    {/* Stats Section Skeleton */}
    <Box sx={{ 
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '8px',
      p: '6px',
      mb: '6px',
      border: `1px solid ${colors.border.light}`
    }}>
      <Skeleton variant="text" width="60%" height={16} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="40%" height={20} />
    </Box>

    {/* Combo Section Skeleton */}
    <Box sx={{ 
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '8px',
      p: '6px',
      mb: '6px',
      border: `1px solid ${colors.border.light}`
    }}>
      <Skeleton variant="text" width="70%" height={16} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="90%" height={20} />
    </Box>

    {/* Duration Section Skeleton */}
    <Box sx={{ 
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '8px',
      p: '6px',
      mb: '6px',
      border: `1px solid ${colors.border.light}`
    }}>
      <Skeleton variant="text" width="75%" height={20} />
    </Box>

    {/* Action Buttons Skeleton */}
    <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
      <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: '6px' }} />
      <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: '6px' }} />
    </Box>
  </Box>
);

const ExperimentColumnSkeleton = ({ color, title, isAlertsMode }) => (
  <Box sx={{
    width: isAlertsMode ? { xs: '100%', md: 'calc(33.33% - 2.67px)' } : { xs: '100%', md: 'calc(20% - 3.2px)' }, // 3 or 5 columns
    flex: '1 1 0',
    minWidth: '0',
    backgroundColor: colors.background.surface,
    borderRadius: '12px',
    border: `2px solid ${colors.border.light}`,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxShadow: `0 2px 8px ${colors.shadow.soft}`
  }}>
    {/* Column Header */}
    <Box sx={{
      p: responsiveSpacing.md,
      borderBottom: `2px solid ${colors.border.light}`,
      backgroundColor: color,
      borderRadius: '10px 10px 0 0'
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Skeleton 
          variant="text" 
          width={120} 
          height={28}
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px'
          }}
        />
        <Skeleton 
          variant="circular" 
          width={32} 
          height={20}
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '16px'
          }}
        />
      </Box>
    </Box>

    {/* Column Content */}
    <Box sx={{
      flex: 1,
      p: responsiveSpacing.md,
      display: 'flex',
      flexDirection: 'column',
      gap: responsiveSpacing.md,
      minHeight: { xs: '300px', md: '800px' }
    }}>
      {/* Skeleton Cards */}
      {[1, 2].map((index) => (
        <ExperimentCardSkeleton key={index} />
      ))}
    </Box>
  </Box>
);

const ExperimentBoardSkeleton = ({ isAlertsMode = false }) => {
  const columns = isAlertsMode ? [
    { color: experimentColors.pending, title: 'Pending' },
    { color: experimentColors.running, title: 'Under Review' }, // Orange for under review
    { color: experimentColors.accepted, title: 'Resolved' } // Blue for resolved
  ] : [
    { color: experimentColors.pending, title: 'Pending' },
    { color: experimentColors.accepted, title: 'Accepted' },
    { color: experimentColors.running, title: 'Running' },
    { color: experimentColors.finished, title: 'Finished' },
    { color: experimentColors.rejected, title: 'Rejected' }
  ];

  return (
    <Box sx={{
      display: 'flex',
      gap: '4px', // Match the new smaller gap
      minHeight: { xs: '500px', md: '1200px' },
      overflowX: 'visible', // Remove horizontal scroll
      p: '4px', // Match the new minimal padding
      backgroundColor: colors.background.default,
      borderRadius: '12px',
      border: `1px solid ${colors.border.light}`,
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {columns.map((column, index) => (
        <ExperimentColumnSkeleton
          key={index}
          color={column.color}
          title={column.title}
          isAlertsMode={isAlertsMode}
        />
      ))}
    </Box>
  );
};

export default ExperimentBoardSkeleton;
